const NEGATIVE = /^-/;

/**
 * Brings the negative vertical margins of a template back to zero.
 *
 * A block pulled up by a negative `margin-top` or `margin-bottom` lands on
 * the block above or below it: the supervision summary had its two rounded
 * boxes overlapping by 13px, the -20px of the first one winning over the
 * half-line paragraph meant to separate them. Nothing in the toolbar writes
 * such a margin, but pasting from Word does, and a template authored long
 * ago only shows the overlap once the spacing changes around it.
 *
 * Horizontal margins are left alone: the templates imported from Word lean
 * on negative left margins for their hanging indents, and a negative
 * horizontal margin never stacks two blocks on top of each other. Reading
 * the sides through the `style` API rather than through a regular expression
 * leaves the browser to break the `margin` shorthand apart, which it has
 * already done.
 *
 * The PDF applies the same rule while it prints, in
 * `clampNegativeVerticalMargins` of anisenior-api, so what is typed and what
 * is printed agree.
 * @function
 *
 * @param {string} html - The template fragment.
 * @returns {string} The fragment without a negative vertical margin.
 */
export const clampNegativeVerticalMargins = (html) => {
  if (typeof html !== 'string' || !html.trim()) return html;
  if (typeof DOMParser === 'undefined') return html;

  const document = new DOMParser().parseFromString(html, 'text/html');
  let changed = false;

  document.body.querySelectorAll('[style]').forEach((element) => {
    ['margin-top', 'margin-bottom'].forEach((side) => {
      if (!NEGATIVE.test(element.style.getPropertyValue(side).trim())) return;
      // The shorthand the browser expanded is rewritten side by side, so the
      // horizontal margin it also carried stays where the author put it.
      element.style.setProperty(side, '0px');
      changed = true;
    });
  });

  return changed ? document.body.innerHTML : html;
};
